'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Sparkles, Shield, Clock, ArrowRight, CheckCircle2, Globe2, FileText, Zap } from 'lucide-react';

const rotatingCountries = [
  { country: 'United States', flag: '🇺🇸', type: 'Tourist B-2' },
  { country: 'United Kingdom', flag: '🇬🇧', type: 'Student Tier 4' },
  { country: 'Schengen Area', flag: '🇪🇺', type: 'Short Stay C' },
  { country: 'Canada', flag: '🇨🇦', type: 'Visitor TRV' },
  { country: 'Australia', flag: '🇦🇺', type: 'Tourist ETA' },
  { country: 'Germany', flag: '🇩🇪', type: 'Work Visa' },
];

const miniChecklist = [
  { label: 'Valid passport (6+ months)', done: true },
  { label: 'Bank statement (3 months)', done: true },
  { label: 'Travel itinerary', done: true },
  { label: 'Hotel booking confirmation', done: true },
  { label: 'Cover letter', done: false, generating: true },
  { label: 'Sponsor letter', done: false, generating: false },
];

export default function HeroSection() {
  const [currentCountry, setCurrentCountry] = useState(0);
  const [coverLetterDone, setCoverLetterDone] = useState(false);

  useEffect(() => {
    const countryInterval = setInterval(() => {
      setCurrentCountry((prev) => (prev + 1) % rotatingCountries?.length);
    }, 2800);
    const clInterval = setTimeout(() => setCoverLetterDone(true), 2000);
    return () => { clearInterval(countryInterval); clearTimeout(clInterval); };
  }, []);

  return (
    <section className="relative pt-28 pb-20 overflow-hidden bg-gradient-to-b from-blue-50/60 via-white to-white">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-100/40 rounded-full blur-3xl" />
        <div className="absolute top-60 -left-20 w-72 h-72 bg-sky-100/50 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-40 bg-indigo-50/60 rounded-full blur-3xl" />
      </div>
      <div className="relative max-w-screen-xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Copy */}
          <div className="space-y-7">
            {/* Eyebrow badge */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-blue-50 border border-blue-100 rounded-full">
              <Sparkles size={13} className="text-blue-500" />
              <span className="text-xs font-semibold text-blue-700 tracking-wide">AI-Powered Visa Assistant</span>
            </div>

            <div className="space-y-4">
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-extrabold text-gray-900 leading-tight tracking-tight">
                Your visa, sorted{' '}
                <span className="gradient-text">in minutes</span>
                <span className="block text-3xl lg:text-4xl xl:text-5xl mt-1 text-gray-800">
                  — not weeks.
                </span>
              </h1>
              <p className="text-lg text-gray-500 leading-relaxed max-w-lg">
                VisaPilot AI generates your complete visa application package — checklist, cover letter, itinerary, and all supporting documents — tailored to your destination and visa type.
              </p>
            </div>

            {/* Dynamic country pill */}
            <div className="flex items-center gap-3 p-3.5 bg-white rounded-xl border border-gray-100 shadow-sm w-fit">
              <span className="text-2xl">{rotatingCountries?.[currentCountry]?.flag}</span>
              <div>
                <p className="text-xs text-gray-400 font-medium">Currently applying for</p>
                <p className="text-sm font-bold text-gray-800">
                  {rotatingCountries?.[currentCountry]?.country} — {rotatingCountries?.[currentCountry]?.type}
                </p>
              </div>
              <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse ml-1" />
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link href="/visa-application-wizard" className="btn-primary text-base py-3 px-6 animate-pulse-glow">
                <Sparkles size={16} />
                Start your application free
              </Link>
              <Link href="#how-it-works" className="btn-secondary text-base py-3 px-6">
                See how it works
                <ArrowRight size={15} />
              </Link>
            </div>

            {/* Trust micro-copy */}
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {[
                { icon: Shield, text: 'Bank-grade encryption' },
                { icon: Clock, text: 'Package ready in 8 min' },
                { icon: Globe2, text: '180+ countries covered' },
              ]?.map((item) => (
                <div key={`trust-${item?.text}`} className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
                  <item.icon size={13} className="text-blue-400" />
                  {item?.text}
                </div>
              ))}
            </div>
          </div>

          {/* Right: Animated wizard preview card */}
          <div className="relative">
            <div className="relative bg-white rounded-2xl border border-gray-100 shadow-wizard overflow-hidden">
              {/* Card header */}
              <div className="bg-gradient-to-r from-blue-600 to-sky-500 px-5 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-white/40" />
                    <div className="w-2 h-2 rounded-full bg-white/40" />
                    <div className="w-2 h-2 rounded-full bg-white/40" />
                  </div>
                  <span className="text-xs text-white/80 font-medium">VisaPilot AI — Document Checklist</span>
                  <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full font-semibold">Step 4 / 9</span>
                </div>
              </div>

              <div className="p-5 space-y-4">
                {/* Country + type */}
                <div className="flex items-center gap-2.5">
                  <span className="text-xl">🇩🇪</span>
                  <div>
                    <p className="text-xs text-gray-400 font-medium">Germany — Schengen Tourist Visa (Type C)</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="w-2/3 h-full bg-blue-500 rounded-full" />
                      </div>
                      <span className="text-xs text-blue-600 font-semibold">67% complete</span>
                    </div>
                  </div>
                </div>

                {/* AI generated checklist */}
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 mb-2.5">
                    <Sparkles size={12} className="text-blue-500" />
                    <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">AI-Generated Checklist</span>
                  </div>
                  {miniChecklist?.map((item, idx) => (
                    <div
                      key={`checklist-item-${idx}`}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                        item?.done ? 'bg-green-50' : item?.generating ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50'
                      }`}
                    >
                      {item?.done ? (
                        <CheckCircle2 size={15} className="text-green-500 shrink-0" />
                      ) : item?.generating ? (
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-blue-400 border-t-transparent animate-spin shrink-0" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 shrink-0" />
                      )}
                      <span className={`text-xs font-medium ${item?.done ? 'text-green-700' : item?.generating ? 'text-blue-700' : 'text-gray-400'}`}>
                        {item?.label}
                        {item?.generating && !coverLetterDone && (
                          <span className="ml-1 text-blue-500 font-semibold">generating...</span>
                        )}
                        {item?.generating && coverLetterDone && (
                          <span className="ml-1 text-green-600 font-semibold">✓ Done</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>

                {/* AI writing indicator */}
                <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-blue-50 to-sky-50 rounded-xl border border-blue-100">
                  <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
                    <Zap size={13} className="text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-blue-800">AI is writing your cover letter</p>
                    <p className="text-xs text-blue-500">Tailored to German embassy requirements</p>
                  </div>
                </div>

                {/* Documents summary */}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { icon: FileText, label: 'Cover Letter', status: 'Generating', color: 'blue' },
                    { icon: Globe2, label: 'Itinerary', status: 'Ready', color: 'green' },
                    { icon: Shield, label: 'Checklist', status: 'Ready', color: 'green' },
                  ]?.map((doc) => (
                    <div key={`doc-${doc?.label}`} className="flex flex-col items-center gap-1 p-2.5 bg-gray-50 rounded-lg">
                      <doc.icon size={16} className={doc?.color === 'green' ? 'text-green-500' : 'text-blue-400'} />
                      <span className="text-xs font-medium text-gray-700 text-center leading-tight">{doc?.label}</span>
                      <span className={`text-xs font-semibold ${doc?.color === 'green' ? 'text-green-600' : 'text-blue-600'}`}>{doc?.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Floating trust badge */}
            <div className="absolute -bottom-4 -left-4 bg-white rounded-xl border border-gray-100 shadow-card px-3.5 py-2.5 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 size={16} className="text-green-600" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-800">98.2% approval rate</p>
                <p className="text-xs text-gray-400">across all applications</p>
              </div>
            </div>

            {/* Floating user count */}
            <div className="absolute -top-4 -right-4 bg-white rounded-xl border border-gray-100 shadow-card px-3.5 py-2.5">
              <p className="text-xs font-bold text-gray-800">52,400+</p>
              <p className="text-xs text-gray-400">packages generated</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}